'use client';

/**
 * Pin/Bookmark toggle — fixează pagina curentă în secțiunea „FIXATE" din sidebar.
 * Apare lângă titlu pe paginile cu PageHeader sau SettingsTabHeader (skip pe
 * login/auth/admin). variant='light' pe headere fără fundal colorat,
 * 'dark' pe gradient/solid-bar.
 *
 * Trăiește în propriul fișier (separat de page-header.tsx) ca să fie stable
 * la HMR Turbopack — adăugarea de export pe un fișier 'use client' poate
 * declanșa cache stale și undefined exports cross-module.
 */

import { usePathname, useSearchParams } from 'next/navigation';
import { Pin, PinOff } from 'lucide-react';
import { useSidebarPrefsStore } from '@/shared/stores/sidebar-prefs-store';

/**
 * URL-uri ale CTA-urilor permanent vizibile sus în sidebar (Document nou,
 * Cheltuială nouă, Recepție nouă). Pin button SKIP pe acestea ca să nu
 * fie pinate, ar duplica intrarea (apar deja ca buton + ar apărea în FIXATE).
 */
const PERMANENT_CTA_HREFS = new Set(['/facturi/nou', '/cheltuieli/nou', '/receptie/nou']);

export function PinPageButton({ variant }: { variant: 'light' | 'dark' }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pinnedHrefs = useSidebarPrefsStore((s) => s.pinnedHrefs);
  const togglePin = useSidebarPrefsStore((s) => s.togglePin);

  if (!pathname) return null;
  if (pathname.startsWith('/login') || pathname.startsWith('/auth') || pathname.startsWith('/admin')) {
    return null;
  }
  // Skip pe CTA permanent vizibile sus — ar duplica intrarea în FIXATE.
  if (PERMANENT_CTA_HREFS.has(pathname)) return null;

  const qs = searchParams?.toString() ?? '';
  const href = qs ? `${pathname}?${qs}` : pathname;
  const isPinned = pinnedHrefs.includes(href) || pinnedHrefs.includes(pathname);
  const Icon = isPinned ? PinOff : Pin;

  const handleClick = () => {
    togglePin(isPinned ? (pinnedHrefs.includes(href) ? href : pathname) : href);
  };

  const baseBtn = 'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors shrink-0';
  const lightCls = isPinned
    ? 'text-amber-500 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'
    : 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-200 dark:hover:bg-neutral-800';
  const darkCls = isPinned
    ? 'text-amber-200 hover:bg-white/15'
    : 'text-white/70 hover:text-white hover:bg-white/15';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${baseBtn} ${variant === 'dark' ? darkCls : lightCls}`}
      aria-label={isPinned ? 'Scoate pagina din Fixate' : 'Fixează pagina în meniu'}
      title={isPinned ? 'Scoate din Fixate' : 'Fixează în meniu'}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
